import {
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Subscription, BehaviorSubject, map, Observable, tap } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { ApiRequestOptions, ApiService } from '../../core/transport/api.service';
import { Endpoints } from '../../core/transport/endpoints';
import { buildExportFilename, exportIdFilter } from '../../core/transport/export-filename';
import { IChatMessage, ProjectMessageCreateRequest, ProjectMessagesResponse } from '../../core/types/message.interface';
import { IAddress, ICabinetCableRequirements, IContactPerson, IProjectBOM, IProjectInProductionPayload, IProjectMarkDeliveredPayload, ISubmitPoPayload, PORequirements, SubmitPoMultipart } from '../../core/types/project-order.interface';
import { SpecialPricingDecisionRequest, SpecialPricingProjectResponse, IProjectContact, ProjectCreateRequest, IProjectListResponse, IProductionProjectListResponse, IProjectCreationAvailability, IProjectItem, IProjectProfileUpdatePayload, QuoteOverridePricingMode } from '../../core/types/project.interface';
import { IProjectQuoteTerms, IPartsPreview, IPartsDraft, QuoteActivityResponse, IQuotePreview, IQuoteEditorDraft, IQuoteEditorPreview, IQuoteRequestIntent, ICustomerQuoteDraft, ICustomerQuotePreview, ICustomerQuoteGenerationRequest } from '../../core/types/project-quote.interface';
import { IProjectCabinet, ProjectQuestionnairePayload } from '../../core/types/project-cabinet.interface';
import { IProjectInstallationConfig, IProjectLaborUpdatePayload, ILaborConfigurationDraft, ILaborConfigurationPreview, IProjectPmUpdatePayload, IPmPricingPreview, IPmConfigurationDraft, IPmConfigurationPreview, IPmSaveRequest, SystemConfigurationRequest, SystemConfigurationPreview } from '../../core/types/project-quote-config.interface';
import { MonitoringConfigurationRequest, MonitoringConfigurationPreview } from '../../core/types/monitoring-configuration.interface';
import { InstallationConfigurationPreview } from '../../core/types/installation-preview.interface';
import type { components } from '../../core/types/generated/project-contract';

import { RoomsAndTabletsPayload, RoomsFormPreview } from '../../core/types/rooms-form.interface';

export type ApprovePoSalesPayload = components['schemas']['AdminProjectSalesApprovalBody'];

export type ApprovePoSupportPayload = components['schemas']['AdminProjectSupportApprovalBody'];

type RejectPoPayload = components['schemas']['AdminProjectRejectPoBody'];

export type MarkInvoicedPayload = components['schemas']['AdminProjectInvoicedBody'];

type ProjectPoFileResponse = components['schemas']['ProjectDocumentLink'];

export type UndoPoApprovalPayload = components['schemas']['UndoPOApprovalRequest'];

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  private readonly api = inject(ApiService);
  private readonly authService = inject(AuthService);

  readonly projects$ = new BehaviorSubject<IProjectListResponse | null>(
    null,
  );

  findMany(params: Partial<any>, options?: ApiRequestOptions): Observable<IProjectListResponse> {
    return this.api.get<IProjectListResponse>(
      Endpoints.projects.list(this.hasAdminProjectsAccess()),
      params,
      options,
    );
  }

  private listRequest?: Subscription;

  findAll(params: Partial<any> = {}): void {
    this.listRequest?.unsubscribe();
    this.projects$.next(null);
    this.listRequest = this.findMany(params).subscribe((response: IProjectListResponse) => {
      this.projects$.next(response);
    });
  }

  sendShippingCommitment(projectId: string, body: components['schemas']['ShippingCommitment'], options?: ApiRequestOptions): Observable<components['schemas']['CommitmentResultRead']> {
    return this.api.post(Endpoints.projects.shippingCommitment(projectId), body, options);
  }

  updateContacts(projectId: string, contacts: IProjectContact[], options?: ApiRequestOptions): Observable<components['schemas']['ProjectContactsResponse']> {
    return this.api.put(Endpoints.projects.contacts(projectId, this.hasAdminProjectsAccess()), {contacts}, options);
  }

  findOne(projectId: string, options?: ApiRequestOptions,): Observable<IProjectItem> {
    return this.api.get<IProjectItem>(Endpoints.projects.one(projectId, this.hasAdminProjectsAccess()),
      undefined,
      options);
  }

  listProductionProjects(
    params: Partial<any> = {},
    options?: ApiRequestOptions,
  ): Observable<IProductionProjectListResponse> {
    return this.api.get<IProductionProjectListResponse>(
      Endpoints.projects.adminProductionList,
      params,
      options,
    );
  }

  getBom(projectId: string, options?: ApiRequestOptions): Observable<IProjectBOM> {
    return this.api.get<IProjectBOM>(Endpoints.projects.adminBom(projectId), undefined, options);
  }

  downloadBom(projectId: string, options?: ApiRequestOptions): Observable<HttpResponse<Blob>> {
    return this.api.downloadResponse(Endpoints.projects.adminBomCsv(projectId), undefined, options);
  }

  exportBulkBom(projectIds: string[], options?: ApiRequestOptions): Observable<HttpResponse<Blob>> {
    const body: components['schemas']['AdminProjectBulkBOMBody'] = { _id: projectIds };
    return this.api.postBlobResponse(
      Endpoints.projects.adminBomBulk,
      body,
      {
        ...options,
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  storeOne(request: ProjectCreateRequest, options?: ApiRequestOptions): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(Endpoints.projects.list(request.scope === 'admin'), request.body, options);
  }

  getPoRequirements(projectId: string, quoteNumber: string, options?: ApiRequestOptions): Observable<PORequirements> {
    return this.api.get<PORequirements>(
      Endpoints.projects.poRequirements(projectId, this.hasAdminProjectsAccess()), {ordered_quote_number:quoteNumber}, options,
    );
  }

  submitPo(
    projectId: string,
    payload: ISubmitPoPayload,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    const formData = new FormData();
    const fields: Omit<SubmitPoMultipart, 'file'> = {
      po_reference: payload.po_reference.trim(), po_total: String(payload.po_total),
      ordered_quote_number: payload.ordered_quote_number.trim(),
      ...(payload.configuration_mode ? {configuration_mode:payload.configuration_mode} : {}),
      ...(payload.po_lines?.length ? {po_lines:JSON.stringify(payload.po_lines)} : {}),
      ...(payload.contacts != null ? {contacts:JSON.stringify(payload.contacts)} : {}),
      ...(payload.shipping_address ? {shipping_address:JSON.stringify(payload.shipping_address)} : {}),
      ...(payload.shipping_attn ? {shipping_attn:JSON.stringify(payload.shipping_attn)} : {}),
      ...(payload.site_address ? {site_address:JSON.stringify(payload.site_address)} : {}),
      ...(payload.site_poc ? {site_poc:JSON.stringify(payload.site_poc)} : {}),
      ...(payload.site_ppe_requirements != null ? {site_ppe_requirements:payload.site_ppe_requirements} : {}),
      ...(payload.cable_requirements?.length ? {cable_requirements:JSON.stringify(payload.cable_requirements)} : {}),
    };
    const markings=(payload.package_markings ?? []).map(line=>line.trim()).filter(Boolean);
    if(markings.length) fields.package_markings=JSON.stringify(markings);
    for(const [name,value] of Object.entries(fields)) if(value != null) formData.append(name,value);
    formData.append('file',payload.file);

    return this.api.upload<IProjectItem>(
      Endpoints.projects.submitPo(projectId, this.hasAdminProjectsAccess()),
      formData,
      options,
    );
  }

  storeQuestionnaire(
    projectId: string,
    cabinetId: string,
    payload: ProjectQuestionnairePayload,
    options?: ApiRequestOptions,
  ): Observable<IProjectCabinet> {
    return this.api.put<IProjectCabinet>(
      Endpoints.projects.cabinet(projectId, cabinetId, this.hasAdminProjectsAccess()),
      payload,
      options,
    );
  }

  updateOne(projectId: string, payload: IProjectProfileUpdatePayload, options?: ApiRequestOptions): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(Endpoints.projects.one(projectId, this.hasAdminProjectsAccess()), payload, options);
  }

  getQuoteTerms(projectId: string, options?: ApiRequestOptions): Observable<IProjectQuoteTerms> {
    return this.api.get<IProjectQuoteTerms>(Endpoints.projects.quoteTerms(projectId, this.hasAdminProjectsAccess()), undefined, options);
  }



  getInstallationConfigPreview(projectId: string, options?: ApiRequestOptions): Observable<InstallationConfigurationPreview> {
    return this.api.get(Endpoints.projects.installationConfigPreview(projectId, this.hasAdminProjectsAccess()), undefined, options);
  }

  previewInstallationConfig(
    projectId: string, config: IProjectInstallationConfig, options?: ApiRequestOptions,
  ): Observable<InstallationConfigurationPreview> {
    return this.api.post(Endpoints.projects.installationConfigPreview(projectId, this.hasAdminProjectsAccess()), config, options);
  }

  updateInstallationConfig(
    projectId: string,
    config: IProjectInstallationConfig,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(
      Endpoints.projects.installationConfig(projectId, this.hasAdminProjectsAccess()),
      config,
      options,
    );
  }

  updateLaborConfig(
    projectId: string,
    payload: IProjectLaborUpdatePayload,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(
      Endpoints.projects.laborConfig(projectId),
      payload,
      options,
    );
  }

  getCreationAvailability(reseller?: string | null, options?: ApiRequestOptions): Observable<IProjectCreationAvailability> {
    const admin = this.hasAdminProjectsAccess();
    return this.api.get<IProjectCreationAvailability>(Endpoints.projects.creationAvailability(admin),
      admin && reseller != null ? { reseller } : undefined, options);
  }

  getPmConfigurationPreview(projectId?: string | null, options?: ApiRequestOptions): Observable<IPmConfigurationPreview> {
    return this.api.get<IPmConfigurationPreview>(Endpoints.projects.pmConfigurationPreview(projectId), undefined, options);
  }

  getLaborConfigurationPreview(projectId: string | null, quoteType?: ILaborConfigurationDraft['quote_type'], options?: ApiRequestOptions): Observable<ILaborConfigurationPreview> {
    return this.api.get(Endpoints.projects.laborConfigurationPreview(projectId), quoteType ? { quote_type: quoteType } : undefined, options);
  }

  previewLaborConfiguration(projectId: string | null, draft: ILaborConfigurationDraft, options?: ApiRequestOptions): Observable<ILaborConfigurationPreview> {
    return this.api.post(Endpoints.projects.laborConfigurationPreview(projectId), draft, options);
  }

  previewPmConfiguration(projectId: string | null, draft: IPmConfigurationDraft, options?: ApiRequestOptions): Observable<IPmConfigurationPreview> {
    return this.api.post<IPmConfigurationPreview>(Endpoints.projects.pmConfigurationPreview(projectId), draft, options);
  }

  previewPmPricing(payload: IProjectPmUpdatePayload, options?: ApiRequestOptions): Observable<IPmPricingPreview> {
    return this.api.post<IPmPricingPreview>(
      Endpoints.projects.pmPricingPreview,
      payload,
      options,
    );
  }

  previewMonitoringConfiguration(payload: MonitoringConfigurationRequest, options?: ApiRequestOptions): Observable<MonitoringConfigurationPreview> {
    return this.api.post(Endpoints.projects.monitoringConfigurationPreview(this.hasAdminProjectsAccess()), payload, options);
  }

  updateMonitoringConfiguration(
    projectId: string, cabinetId: string, payload: MonitoringConfigurationRequest, options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put(Endpoints.projects.monitoringConfiguration(projectId, cabinetId, this.hasAdminProjectsAccess()), payload, options);
  }

  previewSystemConfiguration(payload: SystemConfigurationRequest, options?: ApiRequestOptions): Observable<SystemConfigurationPreview> {
    return this.api.post<SystemConfigurationPreview>(Endpoints.projects.systemConfigurationPreview(this.hasAdminProjectsAccess()), payload, options);
  }

  updateSystemConfiguration(projectId: string, payload: SystemConfigurationRequest, options?: ApiRequestOptions): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(Endpoints.projects.systemConfiguration(projectId, this.hasAdminProjectsAccess()), payload, options);
  }

  updatePmConfig(
    projectId: string,
    payload: IProjectPmUpdatePayload | IPmSaveRequest,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(
      Endpoints.projects.pmConfig(projectId),
      payload,
      options,
    );
  }

  acceptService(projectId: string, options?: ApiRequestOptions): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(Endpoints.projects.acceptService(projectId), {}, options);
  }

  deleteOne(projectId: string, options?: ApiRequestOptions): Observable<void> {
    return this.api.delete<void>(Endpoints.projects.one(projectId, this.hasAdminProjectsAccess()), options);
  }

  exportAll(ids: string[] = []): void {
    void this.api
      .downloadAndSave(
        Endpoints.projects.adminExport,
        buildExportFilename('projects'),
        exportIdFilter(ids),
      )
      .subscribe();
  }

  archiveMany(ids: string[], archived: boolean, options?: ApiRequestOptions): Observable<unknown> {
    return this.api.post(
      Endpoints.projects.archived(this.hasAdminProjectsAccess()),
      {
        _id: ids,
        archived,
      },
      options,
    );
  }

  addTag(projectId: string, tag: string, options?: ApiRequestOptions): Observable<components['schemas']['ProjectTagsResponse']> {
    return this.api.post<components['schemas']['ProjectTagsResponse']>(Endpoints.projects.tags(projectId, this.hasAdminProjectsAccess()), { tag }, options);
  }

  removeTag(projectId: string, tag: string, options?: ApiRequestOptions): Observable<components['schemas']['ProjectTagsResponse']> {
    return this.api.patch<components['schemas']['ProjectTagsResponse']>(Endpoints.projects.tags(projectId, this.hasAdminProjectsAccess()), { tag }, options);
  }

  addCabinet(projectId: string, options?: ApiRequestOptions): Observable<components['schemas']['CabinetCreatedResponse']> {
    return this.api.post<components['schemas']['CabinetCreatedResponse']>(Endpoints.projects.cabinets(projectId, this.hasAdminProjectsAccess()), {}, options);
  }

  archive(
    projectId: string,
    archived: boolean,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.patch<IProjectItem>(
      Endpoints.projects.archiveOne(projectId, this.hasAdminProjectsAccess()),
      {
        archived,
      },
      options,
    );
  }

  removeCabinet(projectId: string, cabinetId: string, options?: ApiRequestOptions): Observable<void> {
    return this.api.delete<void>(Endpoints.projects.cabinet(projectId, cabinetId, this.hasAdminProjectsAccess()), options);
  }

  getRoomsForm(projectId: string, options?: ApiRequestOptions): Observable<RoomsFormPreview> {
    return this.api.get<RoomsFormPreview>(Endpoints.projects.roomsForm(projectId, this.hasAdminProjectsAccess()), undefined, options);
  }

  previewRoomsForm(projectId: string, payload: RoomsAndTabletsPayload, options?: ApiRequestOptions): Observable<RoomsFormPreview> {
    return this.api.post<RoomsFormPreview>(Endpoints.projects.roomsForm(projectId, this.hasAdminProjectsAccess()), payload, options);
  }

  updateRoomsCountAndTablets(
    projectId: string,
    payload: RoomsAndTabletsPayload,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(
      Endpoints.projects.roomsAndTablets(projectId, this.hasAdminProjectsAccess()),
      payload,
      options,
    );
  }

  getQuotePreview(
    projectId: string,
    pricingMode?: QuoteOverridePricingMode, options?: ApiRequestOptions,
  ): Observable<IQuotePreview> {
    const params =
      this.hasAdminProjectsAccess() && pricingMode != null
        ? { pricing_mode: pricingMode }
        : undefined;

    return this.api.get<IQuotePreview>(Endpoints.projects.quotePreview(projectId, this.hasAdminProjectsAccess()),
      params,
      options
    );
  }

  getQuoteEditorPreview(projectId: string, options?: ApiRequestOptions): Observable<IQuoteEditorPreview> {
    return this.api.get<IQuoteEditorPreview>(Endpoints.projects.quoteEditorPreview(projectId, this.hasAdminProjectsAccess()), undefined, options);
  }

  previewQuoteEditorDraft(projectId: string, draft: IQuoteEditorDraft, options?: ApiRequestOptions): Observable<IQuoteEditorPreview> {
    return this.api.post<IQuoteEditorPreview>(Endpoints.projects.quoteEditorPreview(projectId, this.hasAdminProjectsAccess()), draft, options);
  }

  getCustomerQuotePreview(projectId: string, options?: ApiRequestOptions): Observable<ICustomerQuotePreview> {
    return this.api.get<ICustomerQuotePreview>(Endpoints.projects.customerQuotePreview(projectId), undefined, options);
  }

  previewCustomerQuoteDraft(projectId: string, draft: ICustomerQuoteDraft, options?: ApiRequestOptions): Observable<ICustomerQuotePreview> {
    return this.api.post<ICustomerQuotePreview>(Endpoints.projects.customerQuotePreview(projectId), draft, options);
  }

  downloadCustomerQuote(projectId: string, request: ICustomerQuoteGenerationRequest, options?: ApiRequestOptions): Observable<HttpResponse<Blob>> {
    return this.api.postBlobResponse(Endpoints.projects.exportResellerPdf(projectId), request, {
      ...options, headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    });
  }

  requestSpecialPricing(projectId: string, options?: ApiRequestOptions): Observable<IProjectItem> {
    return this.api.post<SpecialPricingProjectResponse>(
      Endpoints.projects.specialPricing(projectId, this.hasAdminProjectsAccess()),
      {},
      options,
    );
  }

  downloadPdf(
    project: IProjectItem,
    includeInstallation?: boolean,
    options?: ApiRequestOptions,
  ): Observable<HttpResponse<Blob>> {
    const params =
      includeInstallation === true
        ? { include_installation: 'true' }
        : undefined;

    return this.api.downloadResponse(Endpoints.projects.exportPdf(project._id, this.hasAdminProjectsAccess()), params, options);
  }

  downloadDatasheets(project: IProjectItem, options?: ApiRequestOptions): Observable<HttpResponse<Blob>> {
    return this.api.downloadResponse(Endpoints.projects.exportDatasheets(project._id, this.hasAdminProjectsAccess()), undefined, options);
  }

  downloadPdfWithOverrides(
    project: IProjectItem,
    payload: IQuoteRequestIntent,
    options?: ApiRequestOptions,
  ): Observable<HttpResponse<Blob>> {

    return this.api.postBlobResponse(
      Endpoints.projects.exportPdf(project._id, this.hasAdminProjectsAccess()),
      payload,
      {
        ...options,
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      },
    );
  }

  approveSpecialPricing(projectId: string, options?: ApiRequestOptions): Observable<IProjectItem> {
    const body: SpecialPricingDecisionRequest = { action: 'approve' };
    return this.api.patch<SpecialPricingProjectResponse>(
      Endpoints.projects.specialPricing(projectId, true),
      body,
      options,
    );
  }

  denySpecialPricing(
    projectId: string,
    reason: string,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    const body: SpecialPricingDecisionRequest = { action: 'deny', reason };
    return this.api.patch<SpecialPricingProjectResponse>(
      Endpoints.projects.specialPricing(projectId, true),
      body,
      options,
    );
  }

  undoPoApproval(projectId: string, approval: 'sales' | 'support', payload: UndoPoApprovalPayload, options?: ApiRequestOptions): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(Endpoints.projects.adminUndoApproval(projectId, approval), payload, options);
  }

  previewPoReview(projectId: string, body: components['schemas']['POReviewDraft'], options?: ApiRequestOptions): Observable<components['schemas']['POReviewData']> {
    return this.api.post(Endpoints.projects.poReview(projectId), body, options);
  }

  approvePoSales(
    projectId: string,
    payload: ApprovePoSalesPayload = {},
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminApproveSales(projectId),
      payload,
      options,
    );
  }

  approvePoSupport(
    projectId: string,
    payload: ApprovePoSupportPayload = {},
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminApproveSupport(projectId),
      payload,
      options,
    );
  }

  rejectPo(
    projectId: string,
    payload: RejectPoPayload,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminRejectPo(projectId),
      payload,
      options,
    );
  }

  markInProduction(
    projectId: string,
    payload: IProjectInProductionPayload,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminMarkInProduction(projectId),
      payload,
      options,
    );
  }

  markDelivered(
    projectId: string,
    payload: IProjectMarkDeliveredPayload = {},
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminMarkDelivered(projectId),
      payload,
      options,
    );
  }

  markInvoiced(
    projectId: string,
    payload: MarkInvoicedPayload,
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminMarkInvoiced(projectId),
      payload,
      options,
    );
  }

  markPaid(projectId: string, options?: ApiRequestOptions): Observable<IProjectItem> {
    return this.api.post<IProjectItem>(
      Endpoints.projects.adminMarkPaid(projectId),
      {},
      options,
    );
  }

  getStringControllerIpFile(projectId: string, options?: ApiRequestOptions): Observable<string> {
    return this.api
      .get<ProjectPoFileResponse>(Endpoints.projects.adminStringControllerIpFile(projectId), undefined, options)
      .pipe(map((response) => response.url));
  }

  getPoFile(projectId: string, options?: ApiRequestOptions): Observable<string> {
    return this.api
      .get<ProjectPoFileResponse>(Endpoints.projects.adminPoFile(projectId), undefined, options)
      .pipe(map((response) => response.url));
  }



  getPartsPreview(projectId: string, query = '', limit = 500, options?: ApiRequestOptions): Observable<IPartsPreview> {
    return this.api.get(Endpoints.projects.partsPreview(projectId, this.hasAdminProjectsAccess()), {query,limit}, options);
  }

  previewParts(projectId: string, draft: IPartsDraft, options?: ApiRequestOptions): Observable<IPartsPreview> {
    return this.api.post(Endpoints.projects.partsPreview(projectId, this.hasAdminProjectsAccess()), draft, options);
  }

  updateProjectParts(
    projectId: string,
    payload: components['schemas']['ProjectUpdatePartsLineItems'], options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(Endpoints.projects.parts(projectId, this.hasAdminProjectsAccess()),
      payload,
      options);
  }

  updateProjectCustomPrice(
    projectId: string,
    payload: components['schemas']['AdminProjectCustomPricingBody'], options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(Endpoints.projects.adminCustomPricing(projectId),
      payload,
      options
    );
  }

  updateProjectShipping(
    projectId: string,
    payload: components['schemas']['AdminProjectCustomPricingBody'], options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(Endpoints.projects.adminCustomPricing(projectId),
      payload,
      options
    );
  }

  updateProjectSpares(
    projectId: string,
    payload: components['schemas']['ProjectUpdateSparesLineItems'],
    options?: ApiRequestOptions,
  ): Observable<IProjectItem> {
    return this.api.put<IProjectItem>(Endpoints.projects.spares(projectId), payload, options);
  }

  getQuoteActivity(
    projectId: string,
    params: { page?: number; limit?: number; status?: string; kind?: 'estimate' | 'customer_quote' } = {},
    options?: ApiRequestOptions,
  ): Observable<QuoteActivityResponse> {
    return this.api.get<QuoteActivityResponse>(
      Endpoints.projects.activityQuotes(projectId, this.hasAdminProjectsAccess()),
      params,
      options,
    );
  }

  getMessages(
    projectId: string,
    params: { page?: number; limit?: number } = {},
    options?: ApiRequestOptions,
  ): Observable<ProjectMessagesResponse> {
    return this.api.get<ProjectMessagesResponse>(
      Endpoints.projects.messages(projectId, this.hasAdminProjectsAccess()),
      params,
      options,
    );
  }

  sendMessage(projectId: string, content: string, options?: ApiRequestOptions): Observable<IChatMessage> {
    const body: ProjectMessageCreateRequest = { content };
    return this.api.post<IChatMessage>(
      Endpoints.projects.messages(projectId, this.hasAdminProjectsAccess()),
      body,
      options,
    );
  }

  markMessagesRead(projectId: string, options?: ApiRequestOptions): Observable<void> {
    return this.api.post<void>(Endpoints.projects.messagesRead(projectId, this.hasAdminProjectsAccess()), {}, options);
  }

  generatePdfFromStoredQuote(
    projectId: string,
    quoteNumber: string,
    options?: ApiRequestOptions,
  ): Observable<HttpResponse<Blob>> {
    const fallbackName = `quote_${quoteNumber}.pdf`;

    return this.api.downloadResponse(
      this.activityQuotePdfEndpoint(projectId, quoteNumber),
      undefined,
      options,
    ).pipe(
      tap((response: HttpResponse<Blob>) => {
        this.api.saveResponse(response, fallbackName, 'application/pdf');
      }),
    );
  }

  private appendJsonField(
    formData: FormData,
    fieldName: string,
    value:
      | IAddress
      | IContactPerson
      | ICabinetCableRequirements[]
      | IProjectContact[]
      | string[]
      | Record<string, unknown>,
  ): void {
    formData.append(fieldName, JSON.stringify(value));
  }

  private activityQuotePdfEndpoint(
    projectId: string,
    quoteNumber: string,
  ): string {
    const normalizedQuoteNumber = encodeURIComponent(quoteNumber);

    return Endpoints.projects.activityQuotePdf(projectId, normalizedQuoteNumber, this.hasAdminProjectsAccess());
  }

  private hasAdminProjectsAccess(): boolean {
    return this.authService.can('adminProjects', 'runtime');
  }
}
